import { ReactNode, useMemo } from 'react';
import type { FC } from 'react';

import { Box, Divider, Drawer, useMediaQuery, Stack, Link,IconButton } from '@mui/material';
import type { Theme } from '@mui/material';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';

import { DiscordIcon } from "../../../components/layout/icons/discord";
import { FacebookIcon } from "../../../components/layout/icons/facebook";
import { TelegramIcon } from "../../../components/layout/icons/telegram";
import { TwitterIcon } from "../../../components/layout/icons/twitter";
import { useAnalytics } from '../../../hooks/dataTracking/useAnalytics';
import { ThemisLogo } from '../icons/themis-logo';
import { SidenavSection } from './SidenavSection';

interface SidenavProps {
  onClose: () => void;
  open: boolean;
}

interface Item {
  title: string;
  children?: Item[];
  chip?: ReactNode;
  icon?: ReactNode;
  path?: string;
  depth?: number;
}

interface Section {
  title?: string;
  items: Item[];
  privateSection: boolean;
}

const getSections = (): Section[] => [
  {
    title: 'Community',
    items: [
      {
        title: 'Vote',
        path: '/',
        depth: 0,
      },
      {
        title: 'My Proposals',
        path: '/my-proposals',
        depth: 0,
      },
    ],
    privateSection: false,
  },
  {
    title: 'Information',
    items: [
      {
        title: 'FAQ',
        path: '/faq',
        depth: 0,
      },
    ],
    privateSection: false,
  },
];

export const Sidenav: FC<SidenavProps> = (props) => {
  const { onClose, open } = props;
  const { sendButtonClickTrackingEvent } = useAnalytics();

  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), {
    noSsr: true,
  });
  const sections = useMemo(() => getSections(), []);

  const { pathname } = useLocation();

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div>
          <Box sx={{ p: 3, pb: 1 }}>
            <NavLink
              to="/"
              onClick={() => sendButtonClickTrackingEvent('Visit Themis homepage')}
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  ml: 1.5,
                  mr: 1.5,
                  mt: '9px',
                }}
              >
                <ThemisLogo />
              </Box>
            </NavLink>
          </Box>
        </div>
        <Divider
          sx={{
            borderColor: '#2D3748', // dark divider
            mt: 2,
          }}
        />
        <Box sx={{ flexGrow: 1 }}>
          {sections.map((section) => {
            return (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              <SidenavSection
                key={section.title}
                path={pathname}
                sx={{
                  mt: 2,
                  '& + &': {
                    mt: 2,
                  },
                }}
                {...section}
              />
            );
          })}
        </Box>
        <Divider
          sx={{
            borderColor: '#2D3748', // dark divider
          }}
        />
        <Box sx={{ px: 3, py: 2 }}>
            <Box>
                <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                >
                <Link
                    component={IconButton}
                    href={`https://facebook.com/pegaxyofficial`}
                    target="_blank"
                    rel="noopener"
                    onClick={() => sendButtonClickTrackingEvent('Visit Facebook Pegaxy link')}
                >
                    <FacebookIcon fontSize="small" sx={{ color: "#ffffff" }} />
                </Link>
                <Link
                    component={IconButton}
                    href={`https://twitter.com/pegaxyofficial`}
                    target="_blank"
                    rel="noopener"
                    onClick={() => sendButtonClickTrackingEvent('Visit Twitter Pegaxy link')}
                >
                    <TwitterIcon fontSize="small" sx={{ color: "#ffffff" }} />
                </Link>
                <Link
                    component={IconButton}
                    href={`https://discord.gg/pegaxy`}
                    target="_blank"
                    rel="noopener"
                    onClick={() => sendButtonClickTrackingEvent('Visit Discord Pegaxy link')}
                >
                    <DiscordIcon fontSize="small" sx={{ color: "#ffffff" }} />
                </Link>
                <Link
                    component={IconButton}
                    href={`https://t.me/pegaxyglobal`}
                    target="_blank"
                    rel="noopener"
                    onClick={() => sendButtonClickTrackingEvent('Visit Telegram Pegaxy link')}
                >
                    <TelegramIcon fontSize="small" sx={{ color: "#ffffff" }} />
                </Link>
                </Stack>
            </Box>
          </Box>

      </Box>
    </>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: 'neutral.900',
            borderRightColor: 'divider',
            borderRightStyle: 'solid',
            borderRightWidth: (theme) => (theme.palette.mode === 'dark' ? 1 : 0),
            color: '#FFFFFF',
            width: 280,
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: 'neutral.900',
          color: '#FFFFFF',
          width: 280,
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

Sidenav.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};
